import {
  type OrientedRect,
  clampCropResizeToCoveredSize,
  clampOrientedResizeToMaxSize,
  computeSingleObjectOrientedResize,
} from '@/lib/transforms/single-object-oriented-resize'
import type { UnifiedSelectionResizeHandle } from '@/lib/transforms/types/unified-selection-resize-handle'
import { computePhotoFillOffsetAfterResize } from '@/lib/photos/photo-fill-resize-offset'
import {
  getCurrentPhotoFillCoveredSize,
  getMaxPhotoFillSize,
  resolveEffectivePhotoFillNaturalSize,
} from '@/lib/photos/photo-fill-resolution'
import type { ShapePhotoFill } from '@/lib/photos/types/shape-photo-fill'
import type { VisualInsetRatios } from '@/lib/shapes/shape-coordinates'
import { geometricToVisualRect } from '@/lib/shapes/shape-visual-bounds'
import type { CropFrameGeom } from './types/crop-frame-geom'

/**
 * Resizes a photo-filled shape's crop frame by dragging one of the 8 whole-shape handles.
 *
 * Every handle — corner or edge — is treated as a pure crop: the photo's own rendered content
 * never rescales or shifts, only the crop window (the frame) changes size, same as
 * `photo-fill-resize-offset.ts`'s edge/crop-drag branch already did on its own. This is a
 * deliberate crop-mode-only departure from legacy `page-editor`'s corner-drag behavior (which
 * scales the photo with the frame on a corner drag — see that file's doc comment for the legacy
 * citation) — the normal Select-tool resize of a photo-filled shape
 * (`unified-selection-resize-session.ts`) still matches legacy exactly, since it calls
 * `computePhotoFillOffsetAfterResize` independently with the real per-handle corner/edge
 * distinction and is untouched by this file.
 *
 * `visualInsetRatios` (see `visualInsetRatiosForShape`) scopes the resulting offset clamp to the
 * shape's tight visual box rather than its full rectangular frame — same reasoning as
 * `computePhotoFillOffsetAfterResize`'s own `visualRect` param. Ratios are scale-invariant, so
 * they're applied to `next`'s own (just-resized) width/height internally, after `next` is known —
 * the caller can't precompute the rect itself since it doesn't know `next` in advance. Omit for
 * the old whole-frame behavior.
 */
export function computeCropFrameResize(params: {
  initial: OrientedRect
  photoFill: ShapePhotoFill
  direction: UnifiedSelectionResizeHandle
  canvasDx: number
  canvasDy: number
  shiftKey: boolean
  aspectLock: { current?: 'width' | 'height' }
  minDimension: number
  visualInsetRatios?: VisualInsetRatios
}): { geom: CropFrameGeom; photoFill: ShapePhotoFill } {
  const {
    initial,
    photoFill,
    direction,
    canvasDx,
    canvasDy,
    shiftKey,
    aspectLock,
    minDimension,
    visualInsetRatios,
  } = params

  const { next: freeNext, useAspectRatio } = computeSingleObjectOrientedResize(
    initial,
    direction,
    canvasDx,
    canvasDy,
    shiftKey,
    aspectLock,
    false,
    minDimension,
    true
  )

  const { naturalWidth, naturalHeight } = resolveEffectivePhotoFillNaturalSize(photoFill)
  const maxSize = getMaxPhotoFillSize(naturalWidth, naturalHeight)

  let next = clampOrientedResizeToMaxSize({
    initial,
    direction,
    next: freeNext,
    maxWidth: maxSize?.maxWidth,
    maxHeight: maxSize?.maxHeight,
    aspectLocked: useAspectRatio,
  })

  const coveredSize = getCurrentPhotoFillCoveredSize(
    initial.width,
    initial.height,
    naturalWidth,
    naturalHeight,
    photoFill.scale
  )

  next = clampCropResizeToCoveredSize({
    initial,
    direction,
    next,
    coveredWidth: coveredSize?.coveredWidth,
    coveredHeight: coveredSize?.coveredHeight,
  })

  const visualRect = visualInsetRatios
    ? geometricToVisualRect(
        { x: 0, y: 0, width: next.width, height: next.height },
        visualInsetRatios
      )
    : undefined

  const { offsetX, offsetY, scale } = computePhotoFillOffsetAfterResize({
    isCornerHandle: false,
    initial,
    next,
    offsetX: photoFill.offsetX,
    offsetY: photoFill.offsetY,
    scale: photoFill.scale,
    naturalWidth,
    naturalHeight,
    visualRect,
  })

  return {
    geom: { x: next.x, y: next.y, width: next.width, height: next.height },
    photoFill: { ...photoFill, offsetX, offsetY, scale },
  }
}
